import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function SignupPage() {
  const { language, t } = useLanguage();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get and store referral code from URL
  const referralCode = searchParams.get('ref');
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    // Store referral code in localStorage for persistence
    if (referralCode) {
      localStorage.setItem('paperpal_referral_code', referralCode);
    }
  }, [referralCode]);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    // Get referral code from URL or localStorage
    const storedReferralCode = referralCode || localStorage.getItem('paperpal_referral_code');

    try {
      await signup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        preferred_language: language,
        referral_code: storedReferralCode || undefined
      });
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to pricing with referral code if present, else onboarding
      if (storedReferralCode) {
        navigate(`/pricing?ref=${storedReferralCode}`, { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="pp-auth min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pp-container">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-10">
              <h1 className="pp-heading-2 text-pp-text" data-testid="signup-title">
                {language === 'fr' ? 'Commencez avec un compte gratuit' : 'Start with a free account'}
              </h1>
              <p className="mt-4 text-pp-text-secondary" data-testid="signup-preview-reassurance">
                {language === 'fr'
                  ? 'Découvrez un aperçu de votre plan personnalisé. Aucune carte bancaire requise.'
                  : 'Explore a preview of your personalized plan. No payment card required.'}
              </p>
              <p className="mt-3 text-sm text-pp-text-muted" data-testid="signup-full-access-price">
                {language === 'fr'
                  ? 'L’accès complet est facultatif : 49 $ CA en un seul paiement, sans abonnement.'
                  : 'Full access is optional: C$49 paid once, with no subscription.'}
              </p>
            </div>

            <div className="pp-card">
              {/* Google Signup */}
              <GoogleSignInButton testId="google-signup-button" className="mb-8" />

              <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-pp-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-4 text-pp-text-muted uppercase tracking-wider">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>

              {/* Email Signup Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-4 rounded-xl bg-pp-error-muted text-pp-error text-sm" data-testid="signup-error">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="pp-label">
                      {t('auth.firstName')}
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="pp-input"
                      required
                      data-testid="firstname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="pp-label">
                      {t('auth.lastName')}
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="pp-input"
                      required
                      data-testid="lastname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="pp-label">
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="pp-input"
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="pp-label">
                    {t('auth.password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      className="pr-12 pp-input"
                      placeholder="••••••••"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-pp-text-muted hover:text-pp-text transition-colors"
                      data-testid="signup-password-visibility-toggle"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="pp-label">
                    {t('auth.confirmPassword')}
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pp-input"
                    placeholder="••••••••"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="pp-btn-primary w-full"
                  data-testid="signup-submit-button"
                >
                  {loading ? t('common.loading') : (language === 'fr' ? 'Créer mon compte gratuit' : 'Create my free account')}
                </button>
              </form>

              <p className="text-center text-pp-text-secondary mt-8">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-pp-navy font-medium hover:underline" data-testid="signup-login-link">
                  {t('auth.loginLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
